import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, Pill, Activity, Save } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

export interface RupturaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const medicamentos = [
  // Medley
  { nome: "Losartana", empresa: "Medley", apresentacao: "Comprimido 50mg" },
  { nome: "Sinvastatina", empresa: "Medley", apresentacao: "Comprimido 20mg" },
  { nome: "Omeprazol", empresa: "Medley", apresentacao: "Cápsula 20mg" },
  { nome: "Atenolol", empresa: "Medley", apresentacao: "Comprimido 25mg" },
  { nome: "Maleato de Enalapril", empresa: "Medley", apresentacao: "Comprimido 10mg" },
  { nome: "Cloridrato de Metformina", empresa: "Medley", apresentacao: "Comprimido 850mg" },
  { nome: "Dipirona", empresa: "Medley", apresentacao: "Solução oral 500mg/mL" },
  { nome: "Paracetamol", empresa: "Medley", apresentacao: "Comprimido 750mg" },
  { nome: "Amoxicilina", empresa: "Medley", apresentacao: "Cápsula 500mg" },
  { nome: "Azitromicina", empresa: "Medley", apresentacao: "Comprimido 500mg" },
  // Sanofi
  { nome: "Dolantina", empresa: "Sanofi", apresentacao: "Solução injetável 50mg/mL" },
  { nome: "Plavix", empresa: "Sanofi", apresentacao: "Comprimido 75mg" },
  { nome: "Lantus", empresa: "Sanofi", apresentacao: "Solução injetável 100UI/mL" },
  { nome: "Toujeo", empresa: "Sanofi", apresentacao: "Solução injetável 300UI/mL" },
  { nome: "Allegra", empresa: "Sanofi", apresentacao: "Comprimido 180mg" },
  { nome: "Renitec", empresa: "Sanofi", apresentacao: "Comprimido 20mg" },
  { nome: "Targocid", empresa: "Sanofi", apresentacao: "Pó para injeção 400mg" },
  { nome: "Stilnox", empresa: "Sanofi", apresentacao: "Comprimido 10mg" },
  { nome: "Lasix", empresa: "Sanofi", apresentacao: "Comprimido 40mg" },
  { nome: "Prozac", empresa: "Sanofi", apresentacao: "Cápsula 20mg" },
];

const categorias = [
  "DERMATOLOGIA",
  "GENÉRICOS",
  "MIP",
  "PRESCRIÇÃO",
  "INST E CUIDADOS ESPECIAIS",
];

export function RupturaModal({ open, onOpenChange }: RupturaModalProps) {
  const [selecoes, setSelecoes] = useState<Record<string, Record<string, boolean>>>({});

  const toggle = (medicamento: string, categoria: string) => {
    setSelecoes((prev) => ({
      ...prev,
      [medicamento]: {
        ...prev[medicamento],
        [categoria]: !prev[medicamento]?.[categoria],
      },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-3xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
        <div className="relative overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                  Ruptura
                </DialogTitle>
                <p className="truncate text-xs font-medium text-white/70">
                  Identifique faltas de produtos por categoria
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto bg-muted/30 p-4 sm:p-5">
          <div className="space-y-3">
            {medicamentos.map((medicamento) => (
              <div
                key={medicamento.nome}
                className="overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tudo-blue/10">
                    <Pill className="h-5 w-5 text-tudo-blue" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-base font-bold text-foreground">
                        {medicamento.nome}
                      </h3>
                      <span className="shrink-0 rounded-full bg-tudo-blue/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-tudo-blue">
                        {medicamento.empresa}
                      </span>
                    </div>
                    <p className="truncate text-xs font-medium text-muted-foreground">
                      {medicamento.apresentacao}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 border-t border-border pt-3 sm:grid-cols-3 lg:grid-cols-5">
                  {categorias.map((categoria) => {
                    const active = !!selecoes[medicamento.nome]?.[categoria];
                    return (
                      <label
                        key={categoria}
                        className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 transition-colors lg:flex-col lg:justify-center lg:text-center ${
                          active ? "border-tudo-blue/40 bg-tudo-blue/5" : "border-border bg-muted/30"
                        }`}
                      >
                        <span
                          className={`text-[10px] font-semibold uppercase leading-tight ${
                            active ? "text-tudo-blue" : "text-muted-foreground"
                          }`}
                        >
                          {categoria}
                        </span>
                        <Switch
                          checked={active}
                          onCheckedChange={() => toggle(medicamento.nome, categoria)}
                          className="shrink-0 data-[state=checked]:bg-tudo-blue"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-tudo-green to-tudo-green/85 px-8 py-5 text-base font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg focus:ring-2 focus:ring-tudo-green focus:ring-offset-2"
            >
              <Save className="h-4 w-4" />
              Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
